import React from 'react'
import PropTypes from 'prop-types'
import { get } from 'lodash'

import { PLACEMENT_POSITIONS } from 'utils/constants/components/popover'
import { LIST_SIZES, LIST_TYPES } from 'utils/constants/components/dropdown'
import { Dropdown } from 'components/dropdown'
import { InfiniteList } from 'components/list'
import { Spinner } from 'components/spinner/Spinner'
import { DefaultRowRenderer } from 'components/dropdown/components/defaultRowRenderer/DefaultRowRenderer'
import { DefaultTriggerRenderer } from 'components/dropdown/components/defaultTriggerRenderer/DefaultTriggerRenderer'
import { DefaultSearchRenderer } from 'components/dropdown/components/defaultSearchRenderer/DefaultSearchRenderer'
import { DefaultErrorRenderer } from 'components/dropdown/components/defaultErrorRenderer/DefaultErrorRenderer'
import { RepositionDropdown } from 'components/dropdown/components/reposition/RepositionDropdown'

import styles from './DropdownInfiniteListWithSearchView.module.scss'

const LIST_MAX_HEIGHT = 300
const LIST_SEARCHABLE_MAX_HEIGHT = 200
const LIST_ROW_HEIGHT_REGULAR = 40
const LIST_ROW_HEIGHT_COMPACT = 24
const LIST_EMPTY_HEIGHT = 80

export const DropdownInfiniteListWithSearchView = props => {
  const {
    // inner
    handleClear,
    handleInputChange,
    handleSearch,
    highlightString,
    searchString,
    handleIsRowSelected,

    // dropdown
    className,
    dataTestId,
    onClose,
    placement,
    width,

    // common
    isLoading,
    isMultiSelect = false,
    size = 'regular',
    titleKey = 'title',
    type = 'primary',

    // trigger
    isDisabled,
    isValid = true,
    multiSelectTitle,
    placeholder,
    triggerClassName,
    triggerElementRenderer = DefaultTriggerRenderer,
    value,

    // search
    debounceTimeout,
    headerRenderer = DefaultSearchRenderer,
    isAutoFocus = true,
    searchLimit = 2,
    searchPlaceholder,

    // list
    closeOnSelect = true,
    isEmptyStateIconShown,
    listItems,
    listMaxHeight = LIST_MAX_HEIGHT,
    listMeta,
    noRowGutter = false,
    onLoadMore,
    onRowClick,
    rowHeight,
    rowRenderer = DefaultRowRenderer,
    totalCount,

    // footer
    error,
    errorClassName,
    footerRenderer = DefaultErrorRenderer,
    showErrorText,

    // reposition
    keysToPositionUpdate = ['isLoading', 'error', 'listItems'],
  } = props
  const listItemsTotalCount = totalCount || get(listMeta, 'totalElements', 0) || listItems.length
  const listRowHeightBySize = size === 'compact' || size === 'small' ? LIST_ROW_HEIGHT_COMPACT : LIST_ROW_HEIGHT_REGULAR
  const listRowHeight = rowHeight || listRowHeightBySize
  const listHeightByRows = listItems.length * listRowHeight
  const maxHeight = handleSearch ? LIST_SEARCHABLE_MAX_HEIGHT : listMaxHeight
  const listHeight = listHeightByRows > maxHeight ? maxHeight : listHeightByRows
  const propsToPositionUpdate = {}

  keysToPositionUpdate.forEach(key => {
    propsToPositionUpdate[key] = props[key]
  })

  return (
    <Dropdown
      dataTestId={dataTestId}
      className={className}
      footerRenderer={footerRenderer({ error, errorClassName, showErrorText })}
      triggerElementRenderer={triggerElementRenderer({
        error,
        isDisabled,
        isMultiSelect,
        isValid,
        multiSelectTitle,
        placeholder,
        size,
        titleKey,
        type,
        value,
        triggerClassName,
      })}
      headerRenderer={headerRenderer({
        debounceTimeout,
        handleInputChange,
        handleSearch,
        handleClear,
        isAutoFocus,
        searchString,
        searchPlaceholder,
        searchLimit,
      })}
      onClose={onClose}
      width={width}
      placement={placement}>
      {({ handlePopoverToggle, scheduleUpdate }) => (
        <div className={styles.listContainer}>
          <RepositionDropdown {...propsToPositionUpdate} scheduleUpdate={scheduleUpdate} />

          {isLoading && <Spinner />}

          {!isLoading && (
            <InfiniteList
              className={size}
              list={listItems}
              listMeta={listMeta}
              loadMore={onLoadMore}
              totalCount={listItemsTotalCount}
              height={listHeight || LIST_EMPTY_HEIGHT}
              onRowClick={({ item }) => {
                onRowClick({ item })
                closeOnSelect && handlePopoverToggle()
              }}
              isIconShown={isEmptyStateIconShown}
              isRowSelected={handleIsRowSelected}
              rowRenderer={({ item }) => rowRenderer({ size, item, searchString: highlightString, titleKey })}
              noRowGutter={noRowGutter}
            />
          )}
        </div>
      )}
    </Dropdown>
  )
}

DropdownInfiniteListWithSearchView.propTypes = {
  // common
  isLoading: PropTypes.bool,
  size: PropTypes.oneOf(LIST_SIZES),
  titleKey: PropTypes.string,
  type: PropTypes.oneOf(LIST_TYPES),

  // dropdown
  className: PropTypes.string,
  dataTestId: PropTypes.string,
  onClose: PropTypes.func,
  placement: PropTypes.oneOf(PLACEMENT_POSITIONS),
  triggerClassName: PropTypes.string,
  triggerElementRenderer: PropTypes.func,
  width: PropTypes.number,

  // trigger
  isDisabled: PropTypes.bool,
  isValid: PropTypes.bool,
  multiSelectTitle: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.string]),

  // list
  closeOnSelect: PropTypes.bool,
  handleIsRowSelected: PropTypes.func,
  height: PropTypes.number,
  isEmptyStateIconShown: PropTypes.bool,
  isMultiSelect: PropTypes.bool,
  listItems: PropTypes.array,
  listMaxHeight: PropTypes.number,
  listMeta: PropTypes.object,
  noRowGutter: PropTypes.bool,
  onLoadMore: PropTypes.func,
  onRowClick: PropTypes.func,
  rowHeight: PropTypes.number,
  rowRenderer: PropTypes.func,
  totalCount: PropTypes.number,

  // search
  debounceTimeout: PropTypes.number,
  handleClear: PropTypes.func,
  handleInputChange: PropTypes.func,
  handleSearch: PropTypes.func,
  isAutoFocus: PropTypes.bool,
  searchLimit: PropTypes.number,
  searchString: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  headerRenderer: PropTypes.func,

  // inner
  highlightString: PropTypes.string,

  // footer
  error: PropTypes.string,
  errorClassName: PropTypes.string,
  footerRenderer: PropTypes.func,
  showErrorText: PropTypes.bool,

  // reposition
  keysToPositionUpdate: PropTypes.array,
}
